<template>
  <div
    :class="[
      'vc-select',
      `vc-select--size--${size}`,
      {
        'vc-select--readonly': readonly,
        'vc-select--disabled': disabled,
        'vc-select--error': error,
        'vc-select--opened': open,
      },
    ]"
  >
    <VcLabel v-if="label" :required="required" :error="error">
      {{ label }}
    </VcLabel>
    <div class="vc-select__container">
      <VcInput
        v-model.trim="searchKeyword"
        maxlength="64"
        class="w-full"
        :disabled="disabled"
        :placeholder="placeholder || label"
        @focus="openDropdown"
        @keyup.enter="applySearch()"
      >
        <template #append>
          <button v-if="searchKeyword" type="button" class="h-full px-4" @click="clearSearch()">
            <VcIcon name="delete-2" size="xs" />
          </button>
          <VcButton icon="search" size="md" @click="applySearch()" />
        </template>
      </VcInput>

      <transition :leave-active-class="`transition duration-${transitionDuration} ease-in`" leave-to-class="opacity-0">
        <div v-if="open && filteredItems.length" class="vc-select__dropdown">
          <ul ref="listElement" class="vc-select__list">
            <li
              v-for="(item, index) in filteredItems"
              :key="index"
              :class="[
                'vc-select__item',
                {
                  'vc-select__item--active': isActiveItem(item),
                },
              ]"
              tabindex="0"
              role="option"
              :aria-selected="isActiveItem(item)"
              @click="selectItem(item)"
              @keyup.enter="selectItem(item)"
            >
              <slot name="item" v-bind="{ item, index, selected }">
                {{ textField && item ? item[textField] : item }}
              </slot>
            </li>
          </ul>
        </div>
      </transition>
    </div>
    <VcInputDetails :show-empty="showEmptyDetails" :message="message" :error="error" />
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { computed, ref, shallowRef } from "vue";
import { onClickOutside } from "@vueuse/core";

const emit = defineEmits<{
  (event: "update:modelValue", value: any): void;
  (event: "change:search", value: any): void;
  (event: "change", value: any): void;
}>();

const props = withDefaults(
  defineProps<{
    label?: string;
    required?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    modelValue?: object | string;
    items: any[];
    size?: "sm" | "md" | "lg" | "auto";
    textField?: string;
    valueField?: string;
    placeholder?: string;
    showEmptyDetails?: boolean;
    error?: boolean;
    message?: string;
  }>(),
  {
    size: "md",
    placeholder: "Search...",
  },
);

const searchKeyword = ref("");
const open = ref(false);
const listElement = shallowRef<HTMLElement | null>(null);
const transitionDuration = 100;

const filteredItems = computed(() => {
  if (!searchKeyword.value) {
    return props.items;
  }
  return props.items.filter((item) => getItemText(item).toLowerCase().includes(searchKeyword.value.toLowerCase()));
});

const selected = computed(() => {
  if (!props.valueField) {
    return props.modelValue;
  }
  return props.items.find((item) => item[props.valueField!] === props.modelValue);
});

function getItemText(item: any): string {
  return props.textField && item ? item[props.textField] : item;
}

function openDropdown() {
  if (!props.disabled) {
    open.value = true;
  }
}

function closeDropdown() {
  open.value = false;
}

function applySearch() {
  emit("change:search", searchKeyword);
  openDropdown();
}

function clearSearch() {
  searchKeyword.value = "";
  emit("change:search", "");
  emit("update:modelValue", "");
}

function selectItem(item: any) {
  const newValue = props.valueField ? item[props.valueField] : item;
  emit("update:modelValue", item);
  emit("change", newValue);

  searchKeyword.value = item.name;

  closeDropdown();
}

function isActiveItem(item: any): boolean {
  const value = props.valueField ? item[props.valueField] : item;
  return value === props.modelValue;
}

onClickOutside(listElement, closeDropdown);
</script>

<style scoped lang="scss">
.vc-select {
  $disabled: "";
  $readonly: "";
  $autocomplete: "";
  $opened: "";
  $error: "";

  @apply flex flex-col;

  &--disabled {
    $disabled: &;
  }

  &--readonly {
    $readonly: &;
  }

  &--autocomplete {
    $autocomplete: &;
  }

  &--opened {
    $opened: &;
  }

  &--error {
    $error: &;
  }

  &__container {
    @apply relative;
  }

  &__button {
    @apply relative flex items-center w-full rounded border bg-additional-50 appearance-none text-left;

    #{$disabled} &,
    &:disabled {
      @apply bg-neutral cursor-not-allowed pointer-events-none;
    }

    #{$readonly} & {
      @apply pointer-events-none;
    }

    #{$error} & {
      @apply border-danger;
    }

    &--opened,
    &:focus {
      @apply outline-none ring-[3px] ring-primary-100;
    }
  }

  &__button-content {
    @apply grow overflow-y-hidden flex flex-col justify-center min-w-0 h-full;

    #{$error} & {
      @apply text-danger;
    }
  }

  &__input {
    @apply w-full cursor-pointer;

    & input {
      @apply cursor-pointer;

      #{$opened} & {
        @apply cursor-auto;
      }
    }
  }

  &__item {
    cursor: pointer;
    padding: 10px;
    border-bottom: 1px solid #eee;
  }

  &__clear {
    @apply flex items-center h-full px-1 text-primary;

    &:hover {
      @apply text-primary-600;
    }
  }

  &__arrow {
    @apply flex items-center h-full pe-3 ps-1 text-neutral-900;

    &:hover {
      @apply text-neutral;
    }

    #{$disabled} & {
      @apply text-neutral;
    }
  }

  &__icon {
    @apply shrink-0 mr-3 fill-neutral-900;

    #{$disabled} & {
      @apply fill-neutral-400;
    }

    #{$readonly} & {
      @apply hidden;
    }
  }

  &__dropdown {
    @apply z-10 overflow-hidden absolute mt-1 w-full bg-additional-50 rounded border border-neutral-100 shadow-lg;
  }

  &__list {
    @apply overflow-auto max-h-60;
  }
}
</style>
